"use client"

import { useState, useEffect } from "react"
import api from "../../utils/api"
import toast from "react-hot-toast"
import { Play, Calendar } from "lucide-react"

const VideoList = () => {
  const [videos, setVideos] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchVideos()
  }, [])

  const fetchVideos = async () => {
    try {
      const response = await api.get("/videos")
      setVideos(response.data.videos)
    } catch (error) {
      toast.error("Failed to fetch videos")
    } finally {
      setLoading(false)
    }
  }

  const getVideoEmbedUrl = (url, type) => {
    if (type === "youtube") {
      const videoId = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/)
      return videoId ? `https://www.youtube.com/embed/${videoId[1]}` : url
    }
    return url
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading videos...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Videos</h1>
          <p className="text-gray-600">Watch our latest content and updates</p>
        </div>

        {videos.length === 0 ? (
          <div className="text-center py-12">
            <Play className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No videos</h3>
            <p className="mt-1 text-sm text-gray-500">No videos have been uploaded yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.map((video) => (
              <div key={video.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="aspect-w-16 aspect-h-9">
                  {video.video_type === "youtube" ? (
                    <iframe
                      src={getVideoEmbedUrl(video.video_url, video.video_type)}
                      title={video.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-48"
                    ></iframe>
                  ) : (
                    <video controls className="w-full h-48 object-cover" src={video.video_url}>
                      Your browser does not support the video tag.
                    </video>
                  )}
                </div>

                <div className="p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{video.title}</h3>
                  <p className="text-gray-600 text-sm mb-3">{video.description}</p>

                  <div className="flex items-center text-xs text-gray-500">
                    <Calendar className="h-4 w-4 mr-1" />
                    <span>{new Date(video.created_at).toLocaleDateString()}</span>
                    <span className="ml-4">By {video.uploaded_by_name}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default VideoList
